import {
  CreateButton,
  DateField,
  DeleteButton,
  EditButton,
  ExportButton,
  FilterDropdown,
  getDefaultSortOrder,
  List,
  ShowButton,
  TagField,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";

import { Divider, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useExport } from "@refinedev/core";
import { ILicence, IMetadataAdp, IVessel } from "interfaces";
import React from "react";
import { formatMoney } from "components/metadata/formatMoney";

export const MetadataAdpList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorters, tableQuery: { isLoading } } = useTable<IMetadataAdp>({
    sorters: {
      initial: [
        {
          field: "id",
          order: "desc",
        },
      ]
    }
  });

  const { triggerExport, isLoading: isLoadingExport } = useExport<IMetadataAdp>(
    {
      mapData: (item) => {
        return {
          "UKHO Licence ID": item?.subscription?.licence?.ukhoLicenceId || "",
          "Product Name": item?.productName || "",
          "Product Group": item?.productGroup || "",
          "ADLL Publication": item?.adllPublication ? "enabled" : "not enabled",
          "ADRS Publication": item?.adrsPublication ? "enabled" : "not enabled",
          "Total Tide Publication": item?.totalTidePublication
            ? "enabled"
            : "not enabled",
          "Currency": item?.currency || "",
          "Subscription Type": item?.subscriptionType || "",
          "Payment Frequency": item?.paymentFrequency || "",
          "Discount Percentage": item?.discountPercentage || "",
          "Annual Flat Fee Per Vessel":
            item?.annualFlatFeePerVessel == null
              ? ""
              : formatMoney(null, item.annualFlatFeePerVessel),
          "Activation Status": item?.activationStatus || "",
          "Licence Type": item?.licenceType || "",
          "Expiry Date": item?.expiryDate || "",
          "TID": item?.tid || "",
          "Startup Key": item?.startupKey || "",
          "Master Machine Name": item?.masterMachineName || "",
          "Master Machine Id": item?.masterMachineId || "",
          "Master Machine Key": item?.masterMachineKey || "",
          "Master Machine Version": item?.masterMachineVersion || "",
          "Backup Machine Name": item?.backupMachineName || "",
          "Backup Machine Id": item?.backupMachineId || "",
          "Backup Machine Key": item?.backupMachineKey || "",
          "Backup Machine Version": item?.backupMachineVersion || "",
        };
      },
      unparseConfig: {
        header: true,
        columns: [
          "UKHO Licence ID",
          "Product Name",
          "Product Group",
          "ADLL Publication",
          "ADRS Publication",
          "Total Tide Publication",
          "Currency",
          "Subscription Type",
          "Payment Frequency",
          "Discount Percentage",
          "Annual Flat Fee Per Vessel",
          "Activation Status",
          "Licence Type",
          "Expiry Date",
          "TID",
          "Startup Key",
          "Master Machine Name",
          "Master Machine Id",
          "Master Machine Key",
          "Master Machine Version",
          "Backup Machine Name",
          "Backup Machine Id",
          "Backup Machine Key",
          "Backup Machine Version",
        ],
      },
    }
  );

  const { selectProps: licenceSelectProps } = useSelect<ILicence>({
    resource: "licence",
    optionLabel: "ukhoLicenceId",
    optionValue: "ukhoLicenceId",
    pagination: {
      mode: "server"
    }
  });

  const { selectProps: imoSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",
    pagination: {
      mode: "server"
    }
  });

  const { selectProps: vesselNameSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "name",
    pagination: {
      mode: "server"
    }
  });

  const columns: ColumnsType<IMetadataAdp> = [
    {
      title: "UKHO Licence",
      dataIndex: ["subscription", "licence", "ukhoLicenceId"],
      key: "subscription.licence.ukhoLicenceId",
      width: 150,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Licence"
            {...licenceSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "IMO Number",
      dataIndex: ["subscription", "licence", "vessel", "imoNumber"],
      key: "subscription.licence.vessel.imoNumber",
      width: 130,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select IMO Number"
            {...imoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Vessel Name",
      dataIndex: ["subscription", "licence", "vessel", "name"],
      key: "subscription.licence.vessel.name",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Vessel"
            {...vesselNameSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Expiry Date",
      dataIndex: "expiryDate",
      key: "expiryDate",
      width: 140,
      render: (value) => <DateField format="DD-MMM-YYYY" value={value} />,
      defaultSortOrder: getDefaultSortOrder("expiryDate", sorters),
      sorter: true,
    },
    {
      title: "Startup Key",
      dataIndex: "startupKey",
      key: "startupKey",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("startupKey", sorters),
      sorter: true,
    },
    {
      title: "ADLL Publication",
      dataIndex: "adllPublication",
      key: "adllPublication",
      width: 120,
      align: "center",
      render: (value) => (
        <TagField
          color={value === "enabled" ? "green" : "orange"}
          value={value}
        />
      ),
    },
    {
      title: "ADRS Publication",
      dataIndex: "adrsPublication",
      key: "adrsPublication",
      width: 120,
      align: "center",
      render: (value) => (
        <TagField
          color={value === "enabled" ? "green" : "orange"}
          value={value}
        />
      ),
    },
    {
      title: "Total Tide Publication",
      dataIndex: "totalTidePublication",
      key: "totalTidePublication",
      width: 140,
      align: "center",
      render: (value) => (
        <TagField
          color={value === "enabled" ? "green" : "orange"}
          value={value}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record.id} />
          <ShowButton hideText size="small" recordItemId={record.id} />
          <DeleteButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        title: "ADP Service",
        subTitle: "active ADP services",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <CreateButton />
              <ExportButton onClick={triggerExport} loading={isLoadingExport} />
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        size="middle"
        scroll={{ x: 1200 }}
        sticky
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
    </List>
  );
};
